import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { Box, Alert, Link, Paper, Grid } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import { resetPassword } from "../../../utils/restAPI";
import { updateLoadingAxios } from "../../../Redux/Reducers/uiSlice";
import { useDispatch } from "react-redux";
import { lightAuthTheme, darkAuthTheme } from "../../../Theme/authTheme";
import AuthLogo from "../AuthLogo";
import { useTheme } from "../../../contexts/ThemeContext";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { IconButton } from "@mui/material";

const ResetUserPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  // Choose the appropriate theme based on current mode
  const authTheme = isDarkMode ? darkAuthTheme : lightAuthTheme;

  // Get token from URL params
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing reset token. Please request a new password reset.");
    }
  }, [token]);

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, "Password must contain at least one uppercase letter, one lowercase letter, and one number")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Please confirm your password"),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!token) {
        setError("Invalid or missing reset token.");
        return;
      }

      try {
        dispatch(updateLoadingAxios(true));
        const response = await resetPassword(token, values.password);
        dispatch(updateLoadingAxios(false));

        if (response && response.success) {
          setIsSuccess(true);
          setError("");
        } else {
          setError("Failed to reset password. Please try again.");
        }
      } catch (error) {
        dispatch(updateLoadingAxios(false));
        setError("Failed to reset password. Please try again.");
      }
    },
  });

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (isSuccess) {
    return (
      <ThemeProvider theme={authTheme}>
        <Box
          sx={{
            minHeight: "100vh",
            backgroundColor: authTheme.palette.background.default,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem 1rem",
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={0}
              sx={{
                padding: { xs: "2rem 1.5rem", sm: "3rem 2.5rem" },
                borderRadius: 3,
                backgroundColor: authTheme.palette.background.paper,
                boxShadow: authTheme.palette.mode === 'dark' 
                  ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                  : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              }}
            >
              {/* Logo */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <AuthLogo />
              </Box>

              {/* Success Message */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem" },
                    fontWeight: 700,
                    color: authTheme.palette.text.primary,
                    mb: 2,
                  }}
                >
                  Password Reset Successfully!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: authTheme.palette.text.secondary,
                    fontSize: "1.125rem",
                    mb: 3,
                  }}
                >
                  Your password has been updated. You can now sign in with your new password.
                </Typography>
                <Alert severity="success" sx={{ borderRadius: 2, mb: 3 }}>
                  Password updated successfully!
                </Alert>
              </Box>

              {/* Sign In Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleBackToLogin}
                sx={{
                  mb: 3,
                  height: "56px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  backgroundColor: authTheme.palette.mode === 'dark' ? "#F9FAFB" : "#1F2937",
                  color: authTheme.palette.mode === 'dark' ? "#111827" : "#FFFFFF",
                  "&:hover": {
                    backgroundColor: authTheme.palette.mode === 'dark' ? "#E5E7EB" : "#374151",
                  },
                }}
              >
                Sign In
              </Button>
            </Paper>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={authTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: authTheme.palette.background.default,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem 1rem",
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              padding: { xs: "2rem 1.5rem", sm: "3rem 2.5rem" },
              borderRadius: 3,
              backgroundColor: authTheme.palette.background.paper,
              boxShadow: authTheme.palette.mode === 'dark' 
                ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <AuthLogo />
            </Box>

            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem" },
                  fontWeight: 700,
                  color: authTheme.palette.text.primary,
                  mb: 2,
                }}
              >
                Reset Your Password
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: authTheme.palette.text.secondary,
                  fontSize: "1.125rem",
                }}
              >
                Enter your new password below.
              </Typography>
            </Box>

            {/* Form */}
            <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 3 }}>
              {/* Error Message */}
              {error && (
                <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Grid container spacing={2}>
                {/* New Password */}
                <Grid size={{ xs: 12 }} >
                  <TextField
                    fullWidth
                    id="password"
                    name="password"
                    label="New Password"
                    placeholder="Enter your new password"
                    type={showPassword ? "text" : "password"}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                          sx={{ color: authTheme.palette.text.secondary }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>

                {/* Confirm Password */}
                <Grid size={{ xs: 12 }} >
                  <TextField
                    fullWidth
                    id="confirmPassword"
                    name="confirmPassword"
                    label="Confirm New Password"
                    placeholder="Confirm your new password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                    helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={toggleConfirmPasswordVisibility}
                          edge="end"
                          sx={{ color: authTheme.palette.text.secondary }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>

                {/* Submit Button */}
                <Grid size={{ xs: 12 }} >
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={!token}
                    sx={{
                      mt: 2,
                      height: "56px",
                      fontSize: "1rem",
                      fontWeight: 600,
                      backgroundColor: authTheme.palette.mode === 'dark' ? "#F9FAFB" : "#1F2937",
                      color: authTheme.palette.mode === 'dark' ? "#111827" : "#FFFFFF",
                      "&:hover": {
                        backgroundColor: authTheme.palette.mode === 'dark' ? "#E5E7EB" : "#374151",
                      },
                      "&:disabled": {
                        backgroundColor: authTheme.palette.mode === 'dark' ? "#4B5563" : "#D1D5DB",
                        color: authTheme.palette.mode === 'dark' ? "#6B7280" : "#9CA3AF",
                      },
                    }}
                  >
                    Reset Password
                  </Button>
                </Grid>

                {/* Back to Login Link */}
                <Grid size={{ xs: 12 }} >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="body2" sx={{ color: authTheme.palette.text.secondary }}>
                      Remember your password?{" "}
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handleBackToLogin}
                        sx={{
                          color: authTheme.palette.secondary.main,
                          textDecoration: "none",
                          fontWeight: 600,
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Back to Login
                      </Link>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ResetUserPassword;
